import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, Users, Shield, Layers, Activity,
  BarChart3, GitBranch, Cpu, DollarSign, Calculator, Send,
  ChevronDown, Phone, HardHat, Calendar, FileText, Headphones,
  Database, ShoppingCart, Scale
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { HSLogo } from '@/components/HSLogo';
import LiveChat from '@/components/LiveChat';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  { name: 'Any Industry', roles: [
    { name: 'Virtual Executive Assistant', score: 75, badges: ['HOT', 'POPULAR'] },
    { name: 'HR Talent Acquisition Specialist', score: 62, badges: ['TRENDING', 'POPULAR'] },
    { name: 'Receptionist', score: 90 },
    { name: 'Lead Qualifier', score: 85 },
    { name: 'Scheduler', score: 88 },
    { name: 'Data Entry Specialist', score: 95 },
  ], icon: Users, score: 83 },
  { name: 'Construction', roles: [
    { name: 'Roofing Estimator', score: 88 },
    { name: 'Production Scheduler', score: 85 },
    { name: 'Permit Coordinator', score: 80 },
    { name: 'Receptionist', score: 90 },
  ], icon: HardHat, score: 88 },
  { name: 'Healthcare', roles: [
    { name: 'Medical Receptionist', score: 85 },
    { name: 'Appointment Scheduler', score: 90 },
    { name: 'Insurance Verification', score: 88 },
    { name: 'Patient Intake', score: 82 },
  ], icon: Headphones, score: 85 },
  { name: 'Real Estate', roles: [
    { name: 'Lead Qualifier', score: 82 },
    { name: 'Showing Coordinator', score: 85 },
    { name: 'Transaction Coordinator', score: 78 },
    { name: 'Listing Manager', score: 80 },
  ], icon: BarChart3, score: 82 },
  { name: 'Insurance', roles: [
    { name: 'Claims Processor', score: 90 },
    { name: 'Supplement Writer', score: 88 },
    { name: 'Policy Admin', score: 85 },
    { name: 'Adjuster Scheduler', score: 92 },
  ], icon: Shield, score: 90 },
  { name: 'Legal', roles: [
    { name: 'Legal Intake', score: 78 },
    { name: 'Document Reviewer', score: 82 },
    { name: 'Paralegal Assistant', score: 72 },
    { name: 'Case Coordinator', score: 76 },
  ], icon: Scale, score: 78 },
  { name: 'E-Commerce', roles: [
    { name: 'Customer Support', score: 84 },
    { name: 'Order Processor', score: 90 },
    { name: 'Catalog Manager', score: 78 },
    { name: 'Returns Handler', score: 86 },
  ], icon: ShoppingCart, score: 84 },
];

const INTEGRATIONS = [
  { name: 'Salesforce', letter: 'SF' },
  { name: 'HubSpot', letter: 'HS' },
  { name: 'Microsoft Dynamics', letter: 'D365' },
  { name: 'Acculynx', letter: 'AX' },
  { name: 'Slack', letter: '#' },
  { name: 'Gmail', letter: 'G' },
  { name: 'Outlook', letter: 'O' },
  { name: 'Stripe', letter: 'S' },
  { name: 'Zapier', letter: 'Z' },
];

const Landing = () => {
  const navigate = useNavigate();
  const [audience, setAudience] = useState('business');
  const [employeeSalary, setEmployeeSalary] = useState(55000);
  const [employeeCount, setEmployeeCount] = useState(1);
  const [consultForm, setConsultForm] = useState({ name: '', company: '', email: '', role: '', description: '' });
  const [earlyForm, setEarlyForm] = useState({ name: '', email: '', role: '', interest: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showFaq, setShowFaq] = useState(null);
  const [activeIndustry, setActiveIndustry] = useState(0);

  const handleConsultSubmit = async () => {
    if (!consultForm.name || !consultForm.email || !consultForm.company) return toast.error('Name, company, and email required');
    setSubmitting(true);
    try {
      await axios.post(`${API}/hub/email/send`, { to: 'info@humanscaled.ai', subject: `Consultation: ${consultForm.company}`, html: `<p>${consultForm.name} (${consultForm.email}) at ${consultForm.company}. Role: ${consultForm.role}. ${consultForm.description}</p>` }).catch(() => {});
      toast.success('A certified operator will contact you shortly.');
      setConsultForm({ name: '', company: '', email: '', role: '', description: '' });
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const handleEarlyAccess = async () => {
    if (!earlyForm.name || !earlyForm.email) return toast.error('Name and email required');
    setSubmitting(true);
    try {
      await axios.post(`${API}/hub/email/send`, { to: 'info@humanscaled.ai', subject: `Operator Early Access: ${earlyForm.name}`, html: `<p>${earlyForm.name} (${earlyForm.email}). Role: ${earlyForm.role}. Interest: ${earlyForm.interest}</p>` }).catch(() => {});
      toast.success('You are on the early access list.');
      setEarlyForm({ name: '', email: '', role: '', interest: '' });
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const businessFaq = [
    { q: 'Who runs the AI?', a: 'Certified human operators manage all AI systems with full accountability.' },
    { q: 'Is this fully autonomous?', a: 'No. HumanScaled operates controlled AI with human oversight at every layer.' },
    { q: 'How is risk controlled?', a: 'Execution caps, credit limits, output validation, PII redaction, and auto-pause on failure.' },
    { q: 'Will this replace staff?', a: 'This replaces repeatable task execution, not human judgment.' },
    { q: 'How is pricing structured?', a: 'Flat monthly operator fees. No per-seat licenses. Usage metered and transparent.' },
  ];
  const operatorFaq = [
    { q: 'What skills are required?', a: 'Domain expertise in your field. No coding. Describe outcomes in plain English.' },
    { q: 'How do I monetize this?', a: 'Set your monthly fee. Serve multiple clients with validated workflows. Margin grows per client.' },
    { q: 'Do I need to code?', a: 'No. The conversational builder generates blueprints from natural language.' },
    { q: 'How is this different from an agency?', a: 'You own the workflow and the client relationship. The platform provides infrastructure.' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden">
      <div className="noise-overlay" />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <HSLogo size={32} />
            <span className="font-black text-xl tracking-tight">HumanScaled</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-muted-foreground hover:text-white" data-testid="nav-login-btn">Login</Button>
            <Button onClick={() => navigate('/signup')} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold px-6 h-10 rounded-sm" data-testid="nav-signup-btn">Get Started</Button>
          </div>
        </div>
      </nav>

      {/* Toggle */}
      <div className="pt-24 pb-4 flex justify-center">
        <div className="inline-flex bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden" data-testid="audience-toggle">
          <button onClick={() => setAudience('business')} className={`px-6 py-2.5 text-sm font-mono transition-all ${audience === 'business' ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-white'}`} data-testid="toggle-business">For Businesses</button>
          <button onClick={() => setAudience('operator')} className={`px-6 py-2.5 text-sm font-mono transition-all ${audience === 'operator' ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-white'}`} data-testid="toggle-operator">For Operators</button>
        </div>
      </div>

      {/* ==================== BUSINESS ==================== */}
      {audience === 'business' && (
        <>
          <section className="pt-8 pb-20" data-testid="hero-business">
            <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center space-y-6">
              <div className="flex justify-center"><HSLogo size={48} /></div>
              <h1 className="text-4xl md:text-6xl font-black tracking-tighter leading-[1.05]">Controlled AI Infrastructure</h1>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto">Reliable AI operations, governed by humans.</p>
              <p className="text-sm text-white/40">Human control. Machine execution. Measured results.</p>
              <div className="pt-4 space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-[0.2em]">For professionals who are AI-curious. Not AI-reckless.</p>
                <p className="text-2xl md:text-3xl font-black text-emerald-400">AI, On a Leash.</p>
              </div>
            </div>
          </section>

          {/* Two Options */}
          <section className="py-16 border-t border-white/5" data-testid="business-options">
            <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 grid md:grid-cols-2 gap-8">
              <div className="bg-[#0a0a0a] border border-emerald-500/20 p-8" data-testid="option-consultation">
                <h3 className="text-lg font-bold mb-2">Work With a Certified Human Operator</h3>
                <p className="text-sm text-muted-foreground mb-6">A qualified operator manages your AI workflows with full accountability.</p>
                <div className="space-y-3">
                  <Input value={consultForm.name} onChange={e => setConsultForm({ ...consultForm, name: e.target.value })} placeholder="Name" className="bg-black/50 border-white/10 rounded-sm" data-testid="consult-name" />
                  <Input value={consultForm.company} onChange={e => setConsultForm({ ...consultForm, company: e.target.value })} placeholder="Company" className="bg-black/50 border-white/10 rounded-sm" data-testid="consult-company" />
                  <Input type="email" value={consultForm.email} onChange={e => setConsultForm({ ...consultForm, email: e.target.value })} placeholder="Email" className="bg-black/50 border-white/10 rounded-sm" data-testid="consult-email" />
                  <Input value={consultForm.role} onChange={e => setConsultForm({ ...consultForm, role: e.target.value })} placeholder="Role" className="bg-black/50 border-white/10 rounded-sm" />
                  <Textarea value={consultForm.description} onChange={e => setConsultForm({ ...consultForm, description: e.target.value })} placeholder="Brief description" className="bg-black/50 border-white/10 rounded-sm min-h-[50px]" />
                  <Button onClick={handleConsultSubmit} disabled={submitting} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-11" data-testid="consult-submit"><Send className="w-4 h-4 mr-2" /> Request Consultation</Button>
                </div>
              </div>
              <div className="bg-[#0a0a0a] border border-white/5 p-8 flex flex-col" data-testid="option-infrastructure">
                <h3 className="text-lg font-bold mb-2">Build Your Own Controlled AI Infrastructure</h3>
                <p className="text-sm text-muted-foreground mb-6">Deploy tailored workflows with monitoring, guardrails, and human oversight.</p>
                <div className="flex-1 space-y-3">
                  {['Conversational workflow builder', 'Production guardrails + monitoring', 'SLA scoring + audit trails', 'CRM + voice + email integrations', 'Client portal with white-label'].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" /><span className="text-muted-foreground">{f}</span></div>
                  ))}
                </div>
                <Button onClick={() => navigate('/signup')} className="w-full bg-white/5 hover:bg-white/10 border border-white/10 font-bold rounded-sm h-11 mt-6">Launch Custom Infrastructure <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </div>
          </section>

          {/* Industries & Roles */}
          <section className="py-16 bg-[#0a0a0a]/50 border-t border-white/5" data-testid="industries-section">
            <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">Roles Replaced by HumanScaled</h2>
              <p className="text-muted-foreground mb-2">Across every industry, repeatable roles are being operated by AI — governed by certified humans.</p>
              <p className="text-sm mb-8"><span className="text-emerald-400 font-mono">Automatable</span> — the percentage AI handles. <span className="text-amber-400 font-mono">H-Factor</span> — the human-governed percentage. Every role needs both.</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {INDUSTRIES.map((ind, i) => {
                  const Icon = ind.icon;
                  return (
                    <button key={i} onClick={() => setActiveIndustry(i)} className={`flex items-center gap-2 px-4 py-2 border transition-all text-sm ${activeIndustry === i ? 'bg-emerald-500 text-black border-emerald-500 font-bold' : 'border-white/10 text-muted-foreground hover:border-emerald-500/50'}`} data-testid={`industry-${i}`}>
                      <Icon className="w-4 h-4" />{ind.name}
                    </button>
                  );
                })}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {INDUSTRIES[activeIndustry].roles.map((role, i) => (
                  <div key={i} className="bg-[#0a0a0a] border border-white/5 p-4 hover:border-emerald-500/30 transition-colors relative">
                    {role.badges && role.badges.map((badge, bi) => (
                      <span key={badge} className={`absolute top-2 text-[9px] font-bold px-1.5 py-0.5 ${
                        badge === 'HOT' ? 'bg-red-500 text-white' :
                        badge === 'TRENDING' ? 'bg-amber-500 text-black' :
                        'bg-violet-500 text-white'
                      }`} style={{ right: bi === 0 ? 8 : (bi * 60 + 8) }}>{badge}</span>
                    ))}
                    <p className="font-medium text-sm mb-2">{role.name}</p>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-emerald-400 font-mono">{role.score}% Automatable</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-amber-400 font-mono">{100 - role.score}% Human Governed</span>
                      </div>
                      <div className="w-full h-1.5 bg-white/5 flex mt-1">
                        <div className="h-full bg-emerald-500" style={{ width: `${role.score}%` }} />
                        <div className="h-full bg-amber-500" style={{ width: `${100 - role.score}%` }} />
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2">{INDUSTRIES[activeIndustry].name}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Payroll Calculator */}
          <section className="py-16 border-t border-white/5" data-testid="savings-calculator">
            <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">Payroll vs. Operator</h2>
              <p className="text-muted-foreground mb-8">See what you are actually spending on roles that can be operated by AI.</p>
              <div className="bg-[#0a0a0a] border border-white/5 p-6 space-y-5 mb-6">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Employee Annual Salary</label>
                  <div className="flex items-center gap-4"><DollarSign className="w-4 h-4 text-muted-foreground" /><Slider value={[employeeSalary]} onValueChange={([v]) => setEmployeeSalary(v)} min={25000} max={120000} step={5000} className="flex-1" /><span className="font-mono text-base font-bold w-24 text-right">${employeeSalary.toLocaleString()}</span></div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Roles to Replace</label>
                  <div className="flex items-center gap-4"><Users className="w-4 h-4 text-muted-foreground" /><Slider value={[employeeCount]} onValueChange={([v]) => setEmployeeCount(v)} min={1} max={10} step={1} className="flex-1" /><span className="font-mono text-base font-bold w-24 text-right">{employeeCount}</span></div>
                </div>
              </div>
              {(() => {
                const mp = Math.round((employeeSalary * employeeCount) / 12); const ben = Math.round(mp * 0.3); const total = mp + ben;
                const opCost = Math.round(employeeCount <= 1 ? 900 : employeeCount <= 3 ? 900 * employeeCount * 0.85 : 900 * employeeCount * 0.75);
                const save = total - opCost;
                return (
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="border border-red-500/20 bg-red-500/5 p-5">
                      <p className="text-xs text-red-400 uppercase tracking-wider mb-3">Employee Payroll</p>
                      <div className="font-mono text-sm space-y-2">
                        <div className="flex justify-between"><span className="text-muted-foreground">Salary ({employeeCount}x)</span><span>${mp.toLocaleString()}/mo</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Benefits (30%)</span><span>${ben.toLocaleString()}/mo</span></div>
                        <div className="flex justify-between border-t border-red-500/20 pt-2 font-bold"><span>Total</span><span className="text-red-400">${total.toLocaleString()}/mo</span></div>
                      </div>
                    </div>
                    <div className="border border-emerald-500/20 bg-emerald-500/5 p-5">
                      <p className="text-xs text-emerald-400 uppercase tracking-wider mb-3">AI Operator</p>
                      <div className="font-mono text-sm space-y-2">
                        <div className="flex justify-between"><span className="text-muted-foreground">Operator fee</span><span>${opCost.toLocaleString()}/mo</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Overhead</span><span className="text-emerald-400">$0</span></div>
                        <div className="flex justify-between border-t border-emerald-500/20 pt-2 font-bold"><span>Total</span><span className="text-emerald-400">${opCost.toLocaleString()}/mo</span></div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </section>

          {/* Integrations */}
          <section className="py-16 bg-[#0a0a0a]/50 border-t border-white/5" data-testid="integrations-section">
            <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">Works With Your Existing Systems</h2>
              <p className="text-muted-foreground mb-8">HumanScaled integrates across enterprise CRMs and SaaS platforms. No disconnected tools. No unmanaged automation.</p>
              <div className="flex flex-wrap gap-4 mb-6">
                {INTEGRATIONS.map(int => (
                  <div key={int.name} className="flex items-center gap-2.5 px-5 py-3 bg-[#0a0a0a] border border-white/10 hover:border-white/20 transition-colors">
                    <div className="w-8 h-8 bg-white/10 flex items-center justify-center font-mono text-xs font-bold text-white/60">{int.letter}</div>
                    <span className="text-sm text-white/60 font-medium">{int.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-16 border-t border-white/5" data-testid="business-faq">
            <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
              <h2 className="text-2xl font-bold tracking-tight mb-8">Frequently Asked Questions</h2>
              <div className="space-y-1">
                {businessFaq.map((item, i) => (
                  <div key={i} className="border border-white/5">
                    <button onClick={() => setShowFaq(showFaq === `b${i}` ? null : `b${i}`)} className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5" data-testid={`faq-b-${i}`}>
                      <span className="text-sm font-medium">{item.q}</span>
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showFaq === `b${i}` ? 'rotate-180' : ''}`} />
                    </button>
                    {showFaq === `b${i}` && <div className="px-4 pb-4 text-sm text-muted-foreground">{item.a}</div>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ==================== OPERATOR ==================== */}
      {audience === 'operator' && (
        <>
          <section className="pt-8 pb-20" data-testid="hero-operator">
            <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 space-y-6">
              <h1 className="text-4xl md:text-6xl font-black tracking-tighter leading-[1.05]">Controlled AI Infrastructure</h1>
              <p className="text-lg text-muted-foreground max-w-xl">Scale your expertise by operating controlled AI systems across multiple clients.</p>
              <p className="text-sm text-white/40">Human control. Machine execution. Measured results.</p>
              <Button onClick={() => navigate('/signup')} className="bg-emerald-500 text-black hover:bg-emerald-400 h-14 px-8 text-lg font-bold rounded-sm" data-testid="hero-cta-btn">Build Your First Agent <ArrowRight className="ml-2 w-5 h-5" /></Button>
            </div>
          </section>

          {/* Income Diversification Visual */}
          <section className="py-16 border-t border-white/5" data-testid="income-section">
            <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">This Is Not About <span className="text-red-400/60 line-through">Quitting Your Job</span></h2>
                <p className="text-xl text-emerald-400 font-medium">This is about income diversification.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-[#0a0a0a] border border-white/5 p-8">
                  <p className="text-red-400/60 text-sm uppercase tracking-wider mb-3">Employment Today</p>
                  <p className="text-2xl font-bold mb-2">Concentrated Income</p>
                  <p className="text-muted-foreground mb-4">One employer. One paycheck. One decision-maker between you and your livelihood.</p>
                  <div className="flex gap-1"><div className="h-8 flex-1 bg-red-500/20 border border-red-500/30 flex items-center justify-center text-xs font-mono text-red-400">100%</div></div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">Single source</p>
                </div>
                <div className="bg-[#0a0a0a] border border-emerald-500/20 p-8">
                  <p className="text-emerald-400 text-sm uppercase tracking-wider mb-3">Fractionalized Income</p>
                  <p className="text-2xl font-bold mb-2">Diversified Portfolio</p>
                  <p className="text-muted-foreground mb-4">Multiple clients. Multiple revenue streams. No single point of failure.</p>
                  <div className="flex gap-1">
                    <div className="h-8 flex-[3] bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-xs font-mono text-emerald-400">Employer</div>
                    <div className="h-8 flex-1 bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-xs font-mono text-violet-400">C1</div>
                    <div className="h-8 flex-1 bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-xs font-mono text-blue-400">C2</div>
                    <div className="h-8 flex-1 bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-xs font-mono text-amber-400">C3</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">Diversified sources</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <div className="border border-emerald-500/10 p-6 text-center">
                  <p className="text-emerald-400 font-mono text-sm mb-3">TIER 1: BUILDER</p>
                  <p className="text-sm text-muted-foreground">Strengthen your value inside your current company. Zero risk. Pure skill amplification.</p>
                </div>
                <div className="border border-violet-500/10 p-6 text-center">
                  <p className="text-violet-400 font-mono text-sm mb-3">TIER 2: SIDE OPERATOR</p>
                  <p className="text-sm text-muted-foreground">Test external demand safely with 1-2 clients. Side income, not replacement income.</p>
                </div>
                <div className="border border-amber-500/10 p-6 text-center">
                  <p className="text-amber-400 font-mono text-sm mb-3">TIER 3: OPEN MARKET</p>
                  <p className="text-sm text-muted-foreground">Expand once stability is proven. Diversification over scale.</p>
                </div>
              </div>
              <div className="text-center">
                <p className="text-lg text-white/80 italic mb-4">"Security does not come from one employer.<br />Security comes from optionality."</p>
                <Button onClick={() => navigate('/signup')} className="bg-emerald-500 text-black hover:bg-emerald-400 h-12 px-8 font-bold rounded-sm">Start Building Your Portfolio <ArrowRight className="ml-2 w-5 h-5" /></Button>
              </div>
            </div>
          </section>

          {/* Industries for Operators */}
          <section className="py-16 bg-[#0a0a0a]/50 border-t border-white/5" data-testid="operator-industries">
            <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">Industries You Can Operate In</h2>
              <p className="text-muted-foreground mb-8">Every role below is posted on job boards right now. Each one is your opportunity.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {INDUSTRIES.map((ind, i) => {
                  const Icon = ind.icon;
                  const hFactor = 100 - ind.score;
                  return (
                    <div key={i} className="bg-[#0a0a0a] border border-white/5 p-5 hover:border-emerald-500/20 transition-colors">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className="w-5 h-5 text-emerald-400" />
                        <h3 className="font-bold text-sm">{ind.name}</h3>
                        <div className="ml-auto text-right">
                          <span className="text-xs font-mono text-emerald-400">{ind.score}%</span>
                          <span className="text-[10px] text-muted-foreground ml-1">Auto</span>
                          <span className="text-xs font-mono text-amber-400 ml-2">{hFactor}%</span>
                          <span className="text-[10px] text-amber-400 ml-1">H-Factor</span>
                        </div>
                      </div>
                      <div className="w-full h-1 bg-white/5 flex mb-3">
                        <div className="h-full bg-emerald-500" style={{ width: `${ind.score}%` }} />
                        <div className="h-full bg-amber-500" style={{ width: `${hFactor}%` }} />
                      </div>
                      <div className="space-y-1">
                        {ind.roles.map((r, j) => (
                          <div key={j} className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">{r.name}</p>
                            <div className="flex gap-2">
                              <span className="text-[10px] font-mono text-emerald-400">{r.score}%</span>
                              <span className="text-[10px] font-mono text-amber-400">{100 - r.score}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Operator Payment Tiers */}
          <section className="py-16 border-t border-white/5" data-testid="operator-pricing">
            <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">Operator Infrastructure Plans</h2>
              <p className="text-muted-foreground mb-8">Live agents. Monitored runtime. Usage credits included.</p>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { name: 'Starter', price: 297, items: ['1 live agent', '500 AI tokens/day', 'Basic monitoring', 'Email integration', 'Community support'], margin: { c: 2, r: 500 } },
                  { name: 'Pro', price: 497, highlighted: true, items: ['5 live agents', '2,000 AI tokens/day', 'Full monitoring + traces', 'All integrations', 'Voice AI access', 'Priority support'], margin: { c: 5, r: 900 } },
                  { name: 'Agency', price: 997, items: ['Unlimited agents', '10,000 AI tokens/day', 'Full observability', 'White-label portal', 'Custom guardrails', 'Dedicated support'], margin: { c: 10, r: 900 } },
                ].map((pkg, i) => (
                  <div key={i} className={`border relative ${pkg.highlighted ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/5 bg-[#0a0a0a]'}`}>
                    {pkg.highlighted && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-emerald-500 text-black text-[10px] font-bold uppercase tracking-wider">Recommended</div>}
                    <div className="p-6">
                      <h3 className="font-bold mb-1">{pkg.name}</h3>
                      <div className="mb-4"><span className="font-mono text-3xl font-bold text-emerald-400">${pkg.price}</span><span className="text-muted-foreground text-sm">/mo</span></div>
                      <ul className="space-y-2 mb-4">
                        {pkg.items.map((f, j) => <li key={j} className="flex items-center gap-2 text-sm"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" /><span className="text-muted-foreground">{f}</span></li>)}
                      </ul>
                      <div className="border-t border-white/5 pt-3 font-mono text-xs text-muted-foreground">
                        <div className="flex justify-between"><span>{pkg.margin.c} clients x ${pkg.margin.r}</span><span className="text-emerald-400">${(pkg.margin.c * pkg.margin.r - pkg.price).toLocaleString()}/mo margin</span></div>
                      </div>
                      <Button onClick={() => navigate('/pricing')} className={`w-full mt-4 h-10 font-bold rounded-sm ${pkg.highlighted ? 'bg-emerald-500 text-black hover:bg-emerald-400' : 'bg-white/5 hover:bg-white/10 border border-white/10'}`}>Get Started</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Early Access */}
          <section className="py-16 border-t border-white/5" data-testid="investment-calculator">
            <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">Investment Calculator</h2>
              <p className="text-muted-foreground mb-8">Average operator bills $1,100/mo per client. See your return at any scale.</p>

              <div className="grid md:grid-cols-4 gap-4 mb-8">
                {[
                  { clients: 1, plan: 'Starter', cost: 297 },
                  { clients: 3, plan: 'Starter', cost: 297 },
                  { clients: 5, plan: 'Pro', cost: 497 },
                  { clients: 10, plan: 'Agency', cost: 997 },
                ].map((s, i) => {
                  const revenue = s.clients * 1100;
                  const margin = revenue - s.cost;
                  const roi = Math.round((margin / s.cost) * 100);
                  return (
                    <div key={i} className={`border p-5 ${i === 2 ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/5 bg-[#0a0a0a]'}`}>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{s.clients} Client{s.clients > 1 ? 's' : ''} · {s.plan}</p>
                      <div className="font-mono text-sm space-y-1.5 mt-3">
                        <div className="flex justify-between"><span className="text-muted-foreground">Revenue</span><span className="text-white">${revenue.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Platform</span><span className="text-red-400">-${s.cost}</span></div>
                        <div className="flex justify-between border-t border-white/10 pt-1.5 font-bold"><span>Margin</span><span className="text-emerald-400">${margin.toLocaleString()}</span></div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/5 text-center">
                        <p className="font-mono text-lg font-bold text-emerald-400">{roi}% ROI</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">Annual: <span className="text-white font-mono">${(margin * 12).toLocaleString()}</span></p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-[#0a0a0a] border border-white/5 p-6">
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Average Client Value</p>
                    <p className="font-mono text-2xl font-bold text-white">$1,100<span className="text-sm text-muted-foreground">/mo</span></p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Break-Even</p>
                    <p className="font-mono text-2xl font-bold text-emerald-400">1 client</p>
                    <p className="text-[10px] text-muted-foreground">covers any plan</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">5-Client Annual</p>
                    <p className="font-mono text-2xl font-bold text-emerald-400">${((5 * 1100 - 497) * 12).toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">net profit/year</p>
                  </div>
                </div>
              </div>

              <p className="text-center text-xs text-muted-foreground mt-4">Based on $1,100 average monthly billing. Your rates may vary by industry and service complexity.</p>
            </div>
          </section>

          {/* Early Access */}
          <section className="py-16 bg-[#0a0a0a]/50 border-t border-white/5" data-testid="early-access">
            <div className="max-w-lg mx-auto px-6 text-center">
              <h2 className="text-2xl font-bold tracking-tight mb-2">Not Ready Yet?</h2>
              <p className="text-muted-foreground mb-6">Join the early access list.</p>
              <div className="space-y-3 text-left">
                <Input value={earlyForm.name} onChange={e => setEarlyForm({ ...earlyForm, name: e.target.value })} placeholder="Name" className="bg-[#0a0a0a] border-white/10 rounded-sm" data-testid="early-name" />
                <Input type="email" value={earlyForm.email} onChange={e => setEarlyForm({ ...earlyForm, email: e.target.value })} placeholder="Email" className="bg-[#0a0a0a] border-white/10 rounded-sm" data-testid="early-email" />
                <Input value={earlyForm.role} onChange={e => setEarlyForm({ ...earlyForm, role: e.target.value })} placeholder="Current Role" className="bg-[#0a0a0a] border-white/10 rounded-sm" />
                <Input value={earlyForm.interest} onChange={e => setEarlyForm({ ...earlyForm, interest: e.target.value })} placeholder="Interest Level" className="bg-[#0a0a0a] border-white/10 rounded-sm" />
                <Button onClick={handleEarlyAccess} disabled={submitting} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-11" data-testid="early-submit">Join Early Access</Button>
              </div>
            </div>
          </section>

          {/* Operator FAQ */}
          <section className="py-16 border-t border-white/5" data-testid="operator-faq">
            <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
              <h2 className="text-2xl font-bold tracking-tight mb-8">Operator FAQ</h2>
              <div className="space-y-1">
                {operatorFaq.map((item, i) => (
                  <div key={i} className="border border-white/5">
                    <button onClick={() => setShowFaq(showFaq === `o${i}` ? null : `o${i}`)} className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5" data-testid={`faq-o-${i}`}>
                      <span className="text-sm font-medium">{item.q}</span>
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showFaq === `o${i}` ? 'rotate-180' : ''}`} />
                    </button>
                    {showFaq === `o${i}` && <div className="px-4 pb-4 text-sm text-muted-foreground">{item.a}</div>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5"><HSLogo size={24} /><span className="font-black text-sm tracking-tight">HumanScaled</span></div>
            <div className="flex flex-wrap gap-5 text-xs text-muted-foreground">
              <a href="/for-business" className="hover:text-white transition-colors">For Business</a>
              <a href="/for-operators" className="hover:text-white transition-colors">For Operators</a>
              <a href="/for-laid-off-tech" className="hover:text-white transition-colors">Career Transition</a>
              <a href="/careers" className="hover:text-white transition-colors">Careers</a>
              <a href="/case-studies" className="hover:text-white transition-colors">Case Studies</a>
              <a href="/pricing" className="hover:text-white transition-colors">Pricing</a>
            </div>
            <p className="text-xs text-muted-foreground">Human control. Machine execution. Measured results.</p>
          </div>
        </div>
      </footer>

      {/* Live Chat */}
      <LiveChat />
    </div>
  );
};

export default Landing;
